import posthog from 'posthog-js'
import { isUnifiedCommerce } from '@shared/constants/index'
import { env } from '@shared/config/env'
import { v5 as uuidV5 } from 'uuid'
import { getFlagOverrides } from './flag-overrides'
export { FeatureFlags } from './feature-flags'
export {
  usePosthogFeatureFlag,
  usePosthogFeatureFlags,
  usePosthogFeatureFlagsReady
} from './use-posthog-feature-flag'

const productionDomains = [
  'https://dashboard.unzer.com',
  'https://dashboard.tillhub.com'
]

// @TODO It would be better if we use import.meta.env.MODE for this than domain checks, however,
// for staging, the app is currently built with development mode, so it does not work.
const environment = productionDomains.includes(window.location.origin)
  ? 'production'
  : 'int'

const apiKey = env.posthogToken

const defaultEventProperties = {
  platform: 'web',
  environment,
  app_name: isUnifiedCommerce() ? 'Unzer Dashboard' : 'Tillhub Dashboard'
}

class PosthogPlugin {
  constructor() {
    this.isInitialized = false
    this.init()
  }

  /**
   * @private
   */
  init() {
    // Locally decided flags, see `flag-overrides.js`. Development builds only, as every
    // deployed environment must resolve flags through real per merchant targeting.
    const flagOverrides = import.meta.env.DEV ? getFlagOverrides() : {}
    const hasFlagOverrides = Object.keys(flagOverrides).length > 0

    if (!apiKey && !hasFlagOverrides) {
      return
    }

    // With no token nothing ever loads flags, so bootstrap them: that supplies the values and
    // marks flags as loaded, which `isFeatureEnabledWhenReady` waits on. Requesting real ones
    // is disabled so the placeholder token never reaches the api.
    const devOptions =
      !apiKey && hasFlagOverrides
        ? {
            bootstrap: { featureFlags: flagOverrides },
            advanced_disable_flags: true
          }
        : {}

    // @docs https://posthog.com/docs/libraries/js
    posthog.init(apiKey || 'development', {
      ...devOptions,
      api_host: 'https://eu.i.posthog.com',
      autocapture: false,
      disable_session_recording: true,
      // These two must be implemented using Router hooks
      // https://posthog.com/docs/libraries/vue-js#capturing-pageviews
      capture_pageview: false,
      capture_pageleave: false,
      capture_exceptions: false,
      disable_scroll_properties: true,
      enable_recording_console_log: false,
      opt_in_site_apps: true,
      // Cost optimization
      // By default, posthog fetches the feature flags on init and then when the user is identified
      // they are fetched again. Since our application is mostly private, we do not care about the initial fetch
      // ( Posthog applies additional charges for each feature flags call )
      // With this configuration option, we will only fetch the feature flags after user is identified.
      advanced_disable_feature_flags_on_first_load: true,
      persistence: 'localStorage+cookie',
      loaded: () => {
        if (this.userAgreedToTracking()) {
          this.enableSessionRecording()
        }
      },
      before_send: (event) => {
        // If we do not have consents for tracking, we will not be capturing any events
        if (!this.userAgreedToTracking()) {
          return null
        }

        Object.assign(event.properties, defaultEventProperties)

        const isAutocaptureEvent = event.event.startsWith('$')

        if (!isAutocaptureEvent) {
          // eslint-disable-next-line
          console.log('[Posthog Event]', event.event, event)
        }

        // If we are not in development mode, we can capture the event
        if (!import.meta.env.DEV) {
          return event
        }

        return null
      }
    })

    // Takes precedence over whatever Posthog resolves, including later refreshes.
    if (apiKey && hasFlagOverrides) {
      posthog.featureFlags.overrideFeatureFlags({ flags: flagOverrides })
    }

    this.isInitialized = true
  }

  enableSessionRecording() {
    if (import.meta.env.DEV) {
      return
    }

    if (posthog.sessionRecordingStarted()) {
      return
    }

    posthog.startSessionRecording()
  }

  userAgreedToTracking() {
    if (isUnifiedCommerce()) {
      return true
    }

    if (!window.CookieScript?.instance) {
      return false
    }

    const state = window.CookieScript.instance.currentState()

    return state.categories?.includes('targeting')
  }

  isFeatureEnabled(key, options) {
    return posthog.isFeatureEnabled(key, options)
  }

  /**
   * Resolves once feature flags are loaded (or immediately if already available).
   */
  isFeatureEnabledWhenReady(key, options) {
    const evaluate = () => this.isFeatureEnabled(key, options)

    if (!this.isInitialized || posthog.featureFlags?.hasLoadedFlags) {
      return Promise.resolve(evaluate())
    }

    return new Promise((resolve) => {
      let unsubscribe = () => {}

      unsubscribe = posthog.onFeatureFlags(() => {
        unsubscribe()
        resolve(evaluate())
      })
    })
  }

  capture(eventName, properties, options) {
    return posthog.capture(eventName, properties, options)
  }

  /**
   * Identifies the user in Posthog, should be called after user is authenticated
   */
  identifyUser(user, username, supportUserId) {
    if (!this.isInitialized) {
      return
    }

    if (!user) {
      throw new Error('Invalid user.')
    }

    // Cost optimization. We identify the user when the user data is fetched.
    // The user is being reset on logout.
    // https://posthog.com/docs/product-analytics/cutting-costs#only-call-identify-once-per-session
    if (posthog._isIdentified()) {
      if (!posthog.featureFlags.hasLoadedFlags) {
        posthog.reloadFeatureFlags()
      }

      return
    }

    const distinctId = this.generateDistinctId(user, username, supportUserId)
    const staffId = user.connected_staff?.[0]?.id

    posthog.identify(
      distinctId,
      {
        environment: environment,
        merchant_name: user.display_name,
        merchant_id: user.id,
        staff_id: staffId || undefined,
        support_user_id: supportUserId || undefined
      },
      {}
    )

    posthog.group('merchant', user.id)
  }

  /**
   * Resets the current posthog user, should be called after logout.
   * Posthog stores the timestamp of the last seen survey date in local storage,
   * which is then used to determine how often user should see surveys.
   * This key is being cleared on `reset`, however, as per request from product,
   * surveys should not be shown on logout/login.
   * To work around this, we will cache the timestamp and set it again after reset, so that
   * the posthog sdk picks it up later.
   * Do note, the `lastSeenSurveyDate` means the user literally saw a survey, it does not
   * necessarily mean they interacted with it ( submitted or dismissed ).
   * Once the survey is seen, it will not display again until the timeout defined in
   * Posthog Dashboard has passed.
   */
  resetUser(resetDeviceId = false) {
    const lastSeenSurveyDate = localStorage.getItem('lastSeenSurveyDate')

    posthog.reset(resetDeviceId)

    if (lastSeenSurveyDate) {
      localStorage.setItem('lastSeenSurveyDate', lastSeenSurveyDate)
    }
  }

  /**
   * Creates distinct id based on the current user state.
   * The output is a uuid, but we will use uuid v5 which generates deterministic ids based on the
   * input.
   * We use a combination of parameters, because we want to have an unique identifier for each user that
   * is logged in with the merchant's account and we want to cover the following cases
   * Merchant is logged in with email/password
   * Subuser is logged in with email/password/organization
   *  - may or may not be connected to a staff member (connected_staff is empty)
   *  - auth.state.username will be set to a different email than user.email
   * Subuser is logged in with email/password/organization and the user is linked to a staff member
   *  - has connected_staff property, which contains the id of the staff resource this user is connected to
   * Support user has logged in
   * @private
   */
  generateDistinctId(user, username, supportUserId) {
    const namespace = env.posthogDistinctIdUuidNamespace

    if (!namespace) {
      throw new Error(
        'Please specify a valid uuid value for VITE_POSTHOG_DISTINCT_ID_UUID_NAMESPACE environment variable.'
      )
    }

    const { id, display_name } = user
    const staffId = user.connected_staff?.[0]?.id

    const inputParams = [id, display_name, username, staffId, supportUserId]

    const inputParamsSerialized = inputParams
      .filter((value) => !!value)
      .join('-')

    return uuidV5(inputParamsSerialized, namespace)
  }
}

const posthogPlugin = new PosthogPlugin()

export const usePosthog = () => posthogPlugin

export default {
  install: (app) => {
    app.config.globalProperties.$posthog = posthogPlugin

    const events = [
      'CookieScriptLoaded',
      'CookieScriptAccept',
      'CookieScriptAcceptAll'
    ]

    events.forEach((event) => {
      window.addEventListener(event, async () => {
        if (posthogPlugin.userAgreedToTracking()) {
          posthogPlugin.enableSessionRecording()
        }
      })
    })
  }
}
